import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Image as ImageIcon, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { SiteListingRow } from '@/hooks/data/useSiteListings';
import { formatDate, formatMoney, formatNumber } from '@/lib/formatLocale';
import { toMs } from '@/lib/appointmentDate';
import { ListingStatePill, listingLocation } from './SiteListingCard';

interface Props {
  listing: SiteListingRow;
  canAdd: boolean;
  onAdd: () => void;
  onOpenChange: (open: boolean) => void;
}

export function SiteListingDialog({ listing: l, canAdd, onAdd, onOpenChange }: Props) {
  const { t } = useTranslation();

  const facts: Array<[string, string]> = [
    [
      t('properties.site.price', 'Preis'),
      l.price != null ? formatMoney(l.price) : t('properties.site.price_on_request', 'Preis auf Anfrage'),
    ],
    [t('properties.site.transaction', 'Art'), l.transaction === 'Miete' ? t('properties.site.rent', 'Miete') : t('properties.site.sale', 'Kauf')],
    [t('properties.site.category', 'Kategorie'), l.category ?? '—'],
    [t('properties.site.area', 'Fläche'), l.area != null ? `${formatNumber(l.area)} m²` : '—'],
    [t('properties.site.rooms', 'Zimmer'), l.rooms != null ? formatNumber(l.rooms) : '—'],
    [t('properties.site.location', 'Lage'), listingLocation(l)],
    [t('properties.site.object_number', 'Objektnummer'), l.objekt_nr ?? '—'],
    [t('properties.site.first_seen', 'Erstmals gesehen'), formatDate(toMs(l.first_seen_at))],
  ];

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="pe-8 font-display leading-snug">{l.title ?? listingLocation(l)}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <ListingStatePill state={l.state} />
            <a
              href={l.site_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-semibold text-ink-2 underline-offset-2 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {t('properties.site.open_on_site', 'Auf der Website öffnen')}
            </a>
          </div>

          {l.state === 'pending' && (
            <p className="rounded-md border border-line bg-paper-2 px-3 py-2 text-sm text-ink-2">
              {t(
                'properties.site.pending_hint',
                'Für dieses Inserat gibt es einen Vorschlag zu einem bestehenden Objekt. Die Administration prüft ihn; bis dahin ist das Hinzufügen gesperrt.',
              )}
            </p>
          )}

          <ListingGallery images={l.images} />

          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {facts.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3 border-b border-line pb-1">
                <dt className="shrink-0 text-ink-3">{label}</dt>
                <dd className="min-w-0 break-words text-end text-ink">{value}</dd>
              </div>
            ))}
          </dl>

          <div className="rounded-md border border-line p-3 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">
              {t('properties.site.listing_contact', 'Kontakt im Inserat')}
            </p>
            <p className="mt-1 text-ink">{l.agent_name ?? '—'}</p>
            <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs">
              {l.agent_phone && (
                <a href={`tel:${l.agent_phone}`} dir="ltr" className="text-ink-2 hover:underline">
                  {l.agent_phone}
                </a>
              )}
              {l.agent_email && (
                <a href={`mailto:${l.agent_email}`} className="break-all text-ink-2 hover:underline">
                  {l.agent_email}
                </a>
              )}
            </p>
          </div>

          {canAdd && l.state !== 'linked' && (
            <div className="flex justify-end">
              <button
                type="button"
                disabled={l.state === 'pending'}
                onClick={onAdd}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                {t('properties.site.add_to_crm', 'Ins CRM übernehmen')}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Fotos des Inserats, direkt vom Justimmo-Speicher; ein fehlendes wird zur leeren Kachel. */
export function ListingGallery({ images }: { images: string[] }) {
  const [broken, setBroken] = useState<Set<string>>(new Set());
  if (images.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      {images.map((src) => (
        <div key={src} className="overflow-hidden rounded-lg border border-line">
          {broken.has(src) ? (
            <div className="flex aspect-[16/10] w-full items-center justify-center bg-paper-2">
              <ImageIcon className="h-8 w-8 text-line-strong" />
            </div>
          ) : (
            <a href={src} target="_blank" rel="noopener noreferrer">
              <img
                src={src}
                alt=""
                loading="lazy"
                onError={() => setBroken((prev) => new Set(prev).add(src))}
                className="aspect-[16/10] w-full object-cover"
              />
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
